import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import Drawer from '../components/Drawer';
import { api } from '../api/client';
import { date } from '../lib/format';

const PAGE = 50;

function whenText(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return `${date(v)} · ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
}

export default function Audit() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [more, setMore] = useState(false);
  const [filter, setFilter] = useState('');
  const [open, setOpen] = useState(false);
  const [entry, setEntry] = useState(null);

  const loadInitial = useCallback(() => {
    setLoading(true);
    setError(null);
    api(`/audit?limit=${PAGE}`)
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setRows(list);
        setMore(list.length === PAGE);
      })
      .catch(() => setError('Could not load the audit log.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadInitial(); }, [loadInitial]);

  async function loadMore() {
    const before = rows[rows.length - 1]?.id;
    if (!before) return;
    try {
      const data = await api(`/audit?limit=${PAGE}&before=${before}`);
      const list = Array.isArray(data) ? data : [];
      setRows((r) => [...r, ...list]);
      setMore(list.length === PAGE);
    } catch {
      setMore(false);
    }
  }

  const visible = filter.trim()
    ? rows.filter((r) =>
        [r.action, r.user_email, r.resource, r.path].join(' ').toLowerCase().includes(filter.toLowerCase()))
    : rows;

  const columns = [
    { key: 'created_at', header: 'When', render: (r) => whenText(r.created_at) },
    { key: 'user_email', header: 'User', render: (r) => r.user_email || '—' },
    { key: 'action', header: 'Action', render: (r) => <span className="mono-sm">{r.action}</span> },
    { key: 'target', header: 'Target', render: (r) => (r.resource_id ? `${r.resource} #${r.resource_id}` : r.resource || '—') },
    {
      key: 'status',
      header: 'Status',
      render: (r) => <span className={'status-pill ' + (r.status >= 400 ? 'status-pill--bad' : 'status-pill--ok')}>{r.status}</span>,
    },
  ];

  function openEntry(row) {
    setEntry(row);
    setOpen(true);
  }

  return (
    <>
      <PageHeader
        title="Audit log"
        actions={
          <input
            className="filter-input"
            placeholder="Filter…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        }
      />
      <section className="panel">
        <DataTable
          columns={columns}
          rows={visible}
          loading={loading}
          error={error}
          getRowKey={(r) => r.id}
          onRowClick={openEntry}
          empty="No activity recorded yet."
        />
        {more && !filter && (
          <div className="loadmore">
            <button className="btn btn--sm" onClick={loadMore}>Load more</button>
          </div>
        )}
      </section>

      <Drawer open={open} title="Audit entry" onClose={() => setOpen(false)}
        footer={<button className="btn" onClick={() => setOpen(false)}>Close</button>}>
        {entry && (
          <>
            <div className="kv"><span>When</span><span>{whenText(entry.created_at)}</span></div>
            <div className="kv"><span>User</span><span>{entry.user_email || '—'}</span></div>
            <div className="kv"><span>Action</span><span className="mono-sm">{entry.action}</span></div>
            <div className="kv"><span>Target</span><span>{entry.resource_id ? `${entry.resource} #${entry.resource_id}` : entry.resource || '—'}</span></div>
            <div className="kv"><span>Request</span><span className="mono-sm">{entry.method} {entry.path}</span></div>
            <div className="kv"><span>Status</span><span>{entry.status}</span></div>
            <div className="kv"><span>IP</span><span>{entry.ip || '—'}</span></div>
            <div className="kv"><span>Business</span><span>{entry.business_id ?? '—'}</span></div>
            <h3 className="form-section">Payload</h3>
            <pre className="audit-detail">{entry.detail ? JSON.stringify(entry.detail, null, 2) : '—'}</pre>
          </>
        )}
      </Drawer>
    </>
  );
}
